import { CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';

export interface ContactCardField {
  label: string;
  value: ReactNode;
  // Optional plain-text tooltip; the visible value wraps on the mobile card.
  title?: string;
}

interface ContactCardProps {
  dataCy?: string;
  // Card heading — the record's identity (address/phone type, or the email itself).
  title: ReactNode;
  // Show a "Primary" pill (email/phone only; addresses have no primary flag).
  isPrimary?: boolean;
  fields: ContactCardField[];
  onEdit: () => void;
  onDelete: () => void;
  // Record identity (the email, or the address/phone type) used to compose the
  // Edit/Delete screen-reader labels. ContactCard owns the "Edit, X" / "Delete X"
  // phrasing so callers pass the bare value and a missing identity degrades to
  // just "Edit" / "Delete" instead of announcing the literal word "undefined".
  label?: string;
}

// [MCV-1332] Stacked-card rendering of a single contact record, shown below the
// table breakpoint where the multi-column tables would otherwise overflow/scroll
// horizontally on a phone. Edit/Delete use real <button>s so the card is fully
// keyboard- and screen-reader-operable.
export const ContactCard = ({
  dataCy,
  title,
  isPrimary,
  fields,
  onEdit,
  onDelete,
  label,
}: ContactCardProps) => (
  <li data-cy={dataCy} className="list-none border-2 border-gray-2 rounded-md p-3">
    <div className="flex flex-col gap-2">
      <span className="font-medium text-content-secondary [overflow-wrap:anywhere]">{title}</span>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isPrimary && (
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary-t-900 text-primary-50 text-xs px-2 py-0.5">
            <CheckIcon className="size-3.5" aria-hidden="true" />
            Primary
          </span>
        )}
        <div className="ml-auto shrink-0 flex items-center gap-2 text-sm font-medium">
          <button
            type="button"
            className="inline-flex min-h-11 min-w-11 items-center justify-center text-primary-50 hover:text-primary-200 cursor-pointer"
            onClick={onEdit}
          >
            Edit{label && <span className="sr-only">, {label}</span>}
          </button>
          <button
            type="button"
            className="inline-flex min-h-11 min-w-11 items-center justify-center text-error hover:text-error-100 cursor-pointer"
            onClick={onDelete}
          >
            <TrashIcon className="size-4 inline" />
            <span className="sr-only">{label ? `Delete ${label}` : 'Delete'}</span>
          </button>
        </div>
      </div>
    </div>
    {fields.length > 0 && (
      <dl className="mt-2 border-t border-gray-2 divide-y divide-gray-2">
        {fields.map((field) => (
          <div key={field.label} className="flex flex-col gap-1 py-2 text-sm">
            <dt className="shrink-0 text-content-secondary">{field.label}</dt>
            <dd
              title={field.title}
              className="min-w-0 [overflow-wrap:anywhere] text-content-secondary"
            >
              {field.value}
            </dd>
          </div>
        ))}
      </dl>
    )}
  </li>
);
